/* WebComponentTimedMessageBar component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Prop, Watch, h } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import TimedMessageBarTemplate from './cwc-timed-message-bar.template';
import { MessageState } from './message-state.enum';
import { AbstractComponent } from '@cewe-designsystem/abstract';

@Component({
  tag: 'cwc-timed-message-bar',
  styleUrl: './cwc-timed-message-bar.scss',
  shadow: true,
})
export class TimedMessageBarComponent {
  /**
   * element
   */
  @Element() element: HTMLElement;
  @Prop() from: string; // start datetime 2023-10-31T00:00:00
  @Prop() to: string; // end datetime 2023-10-31T23:59:59
  @Prop({ reflect: true, mutable: true }) state: MessageState = MessageState.Invalid; // current visible state
  @Prop() url: string;
  @Prop() image: string;
  /* eslint-disable-next-line no-magic-numbers */
  checkIntervalEveryMS: number = 1000 * 60; // re-evaluate every 60 seconds (every minute)
  checkInterval: ReturnType<typeof setInterval>; // timer
  datetimeStart: Date;
  datetimeEnd: Date;

  /**
   * Watch attributes for changes, reset if change is detected (for quick visibility testing, see the component and make screenshots for example)
   */
  @Watch('from')
  @Watch('to')
  watchAttributesAndReset() {
    this.reset();
    this.init();
  }

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It is important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {
    this.init();
  }

  /**
   * Called every time the component is disconnected from the DOM, i.e., it calls be dispatched more than once
   * DO not confuse with a "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {
    this.reset();
  }

  /**
   * Reset the element
   */
  reset() {
    this.element.removeAttribute('debug');
    clearInterval(this.checkInterval);
  }

  /**
   * Initializes the element (validates from/to dates, and activates a minutely interval)
   *
   * @returns void
   */
  init() {
    this.datetimeStart = null;
    this.datetimeEnd = null;

    // Check: start datetime (from)
    if (this.from) {
      if (!this.hasValidDatePattern(this.from)) {
        this.state = MessageState.Invalid;
        this.element.setAttribute('debug', 'invalid date pattern: from');
        return;
      } else {
        this.datetimeStart = new Date(this.from);

        if (!this.isValidDate(this.datetimeStart)) {
          this.state = MessageState.Invalid;
          this.element.setAttribute('debug', 'invalid date: from');
          return;
        }
      }
    }

    // Check: end datetime (to)
    if (this.to) {
      if (!this.hasValidDatePattern(this.to)) {
        this.state = MessageState.Invalid;
        this.element.setAttribute('debug', 'invalid date pattern: to');
        return;
      } else {
        this.datetimeEnd = new Date(this.to);

        if (!this.isValidDate(this.datetimeEnd)) {
          this.state = MessageState.Invalid;
          this.element.setAttribute('debug', 'invalid date: to');
          return;
        }
      }
    }

    // Check: 'from' must always be less than 'to' if given
    if (this.datetimeStart !== null && this.datetimeEnd !== null && this.datetimeEnd < this.datetimeStart) {
      this.state = MessageState.Invalid;
      this.element.setAttribute('debug', 'from must be lower then to');
      return;
    }

    // Evaluate the visibility now
    this.evaluateState();

    // Evaluate the visibility after every minute:
    // if a customer is browsing the website, and the message expires at midnight, it should automatically disappear
    this.checkInterval = setInterval(() => {
      this.evaluateState();
    }, this.checkIntervalEveryMS);
  }

  /**
   * Check if the value matches the datetime pattern '2023-10-31T23:59:59'
   *
   * @param value
   * @returns
   */
  hasValidDatePattern(value: string): boolean {
    const regex = new RegExp(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(:(\d{2}))(\+\d{2}:\d{2})?$/);
    return regex.test(value);
  }

  /**
   * Check if the given object is a valid Date (DateTime) object
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/toString#using_tostring_to_detect_object_class
   *
   * @param value
   */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  isValidDate(value: any): boolean {
    if (Object.prototype.toString.call(value) === '[object Date]') {
      // It is a date, but Safari can still give a 'isNaN' (for example when the 'T' is missing from '2023-10-23T00:01', like this: '2023-10-23 00:01')
      if (!isNaN(value)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Determines the state/visibility of the messagebar
   * @returns boolean
   */
  evaluateState(): void {
    // Note: Dates are compared with the local time of the customer's device.
    // Possible future improvement: Compare to a REAL date/time, call external API (preferably async and as a fallback only, so 99% of the customers will not be bothered with unnecessary waiting time)
    const now = new Date();

    if (this.datetimeEnd !== null && now >= this.datetimeEnd) {
      this.state = MessageState.Expired;
      return;
    }

    if (this.datetimeStart !== null && now < this.datetimeStart) {
      this.state = MessageState.Planned;
      return;
    }
    this.state = MessageState.Active;
  }

  /**
   * render
   */
  render() {
    return this.state === MessageState.Active ? <TimedMessageBarTemplate controller={this} /> : null;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(TimedMessageBarComponent.prototype, AbstractComponent.prototype);

export interface TimedMessageBarComponent extends AbstractComponent {}
